import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  PLATFORM_ID,
  resource,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { WidgetService } from '../../services/widget.service';
import { TrialBookingService } from '../../services/trial-booking.service';
import { LeadCaptureV2Component } from '../lead-capture-v2/lead-capture-v2.component';
import { LeadCaptureWidgetSubmission } from '../../interfaces/lead-capture-submission.interface';
import { extractWidgetApiErrorMessage } from '../../services/widget-api-error.util';
import {
  applyDefaultPurchasePortalTheme,
  applyPurchasePortalThemeToHost,
  buildPurchasePortalTheme,
  parsePurchasePortalTheme,
} from '../../services/widget-purchase-portal-theme.util';
import { WidgetRecaptchaFlowService } from '../../services/widget-recaptcha-flow.service';
import { WidgetRecaptchaSubmitPayload } from '../../services/recaptcha/widget-recaptcha.types';
import { WidgetRecaptchaV2SectionComponent } from '../../components/widget-recaptcha-v2-section/widget-recaptcha-v2-section.component';
import { WidgetProcessingOverlayComponent } from '../../components/widget-processing-overlay/widget-processing-overlay.component';
import { WIDGET_RECAPTCHA_V2_HINT_TRIAL_BOOKING } from '../../constants/checkout-security.constants';
import {
  normalizeTrialStartDateForApi,
  localTodayDateInputValue,
} from '../../utils/trial-booking-datetime.util';
import { WidgetResponse } from '../../interfaces/widget.interface';

function formatTrialBookingWidgetLoadError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return (
      extractWidgetApiErrorMessage(error.error) ??
      'Failed to load widget. Please try again later.'
    );
  }

  if (error instanceof Error) {
    if (error.message.includes('Invalid widget type')) {
      return 'This widget is not configured for trial booking. Please check the link or contact the studio.';
    }
    if (error.message === 'Widget ID is required') {
      return 'Widget ID is missing. Please use a valid trial booking link.';
    }
    return error.message;
  }

  return 'Failed to initialize widget. Please try again later.';
}

@Component({
  selector: 'ufp-trial-booking',
  templateUrl: './trial-booking.component.html',
  styleUrls: ['./trial-booking.component.css'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    LeadCaptureV2Component,
    WidgetRecaptchaV2SectionComponent,
    WidgetProcessingOverlayComponent,
  ],
})
export class TrialBookingComponent {
  readonly widgetId = input('');

  private readonly leadCapture = viewChild(LeadCaptureV2Component);
  private readonly widgetService = inject(WidgetService);
  private readonly trialBookingService = inject(TrialBookingService);
  readonly recaptchaFlow = inject(WidgetRecaptchaFlowService);
  readonly recaptchaV2Hint = WIDGET_RECAPTCHA_V2_HINT_TRIAL_BOOKING;
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);

  readonly trialBookingDate = signal('');
  readonly selectedTenantUuid = signal<string | null>(null);
  readonly surveyFormComplete = signal(false);
  readonly submitSuccess = signal(false);
  readonly submissionError = signal<string | null>(null);
  readonly isSubmitting = signal(false);

  readonly widgetResource = resource({
    params: () => {
      if (!isPlatformBrowser(this.platformId)) {
        return undefined;
      }
      const id = this.widgetId().trim();
      return id ? { id } : undefined;
    },
    loader: async ({ params }) => {
      if (!params?.id) {
        throw new Error('Widget ID is required');
      }
      try {
        const details = await firstValueFrom(
          this.widgetService.getWidgetDetails(params.id),
        );
        if (details.widget_type !== 'TrialBooking') {
          throw new Error('Invalid widget type. Expected TrialBooking.');
        }
        return details;
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          error.message.includes('Invalid widget type')
        ) {
          throw error;
        }
        if (error instanceof HttpErrorResponse) {
          throw new Error(formatTrialBookingWidgetLoadError(error));
        }
        if (error instanceof Error) {
          throw error;
        }
        throw new Error(formatTrialBookingWidgetLoadError(error));
      }
    },
  });

  /** Avoid reading `widgetResource.value()` while the resource is in an error state (throws NG/resource errors). */
  readonly loadedWidgetConfig = computed((): WidgetResponse | null => {
    if (!this.widgetResource.hasValue() || this.widgetResource.error()) {
      return null;
    }
    return this.widgetResource.value();
  });

  readonly widgetConfigReady = computed(() => this.loadedWidgetConfig() !== null);

  /** True until widget metadata is loaded (avoids showing date/submit while resource is idle or loading). */
  readonly isWidgetShellLoading = computed(() => {
    if (this.widgetLoadErrorMessage() || this.submitSuccess()) {
      return false;
    }
    const id = this.widgetId().trim();
    if (!id) {
      return false;
    }
    return this.widgetResource.isLoading() || !this.widgetConfigReady();
  });

  readonly companyUuid = computed(
    () => this.loadedWidgetConfig()?.widget_company_uuid ?? null,
  );

  readonly defaultTenantUuid = computed(
    () =>
      this.loadedWidgetConfig()?.widget_enabled_tenants?.[0]?.tenant_uuid ??
      '',
  );

  readonly effectiveTenantUuid = computed(() => {
    const fromSurvey = this.selectedTenantUuid()?.trim();
    if (fromSurvey) {
      return fromSurvey;
    }
    return this.defaultTenantUuid().trim() || null;
  });

  readonly minTrialDateIso = computed(() => localTodayDateInputValue());

  readonly canSubmit = computed(
    () =>
      this.surveyFormComplete() &&
      !!this.trialBookingDate().trim() &&
      !!this.effectiveTenantUuid() &&
      !this.isSubmitting() &&
      this.widgetConfigReady(),
  );

  readonly widgetLoadErrorMessage = computed(() => {
    const err = this.widgetResource.error();
    if (!err) {
      return null;
    }
    return formatTrialBookingWidgetLoadError(err);
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      applyDefaultPurchasePortalTheme(this.elementRef.nativeElement);
    }

    effect(() => {
      const details = this.loadedWidgetConfig();
      if (!details?.widget_company_uuid) {
        return;
      }
      void this.applyCompanyTheme(details.widget_company_uuid);
    });

    effect(() => {
      const company = this.companyUuid();
      const tenant = this.effectiveTenantUuid();
      if (company && tenant) {
        void this.recaptchaFlow.prepareForTenant(company, tenant);
      }
    });
  }

  onTrialDateInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.trialBookingDate.set(value);
  }

  onTourFormCompleteChange(complete: boolean): void {
    this.surveyFormComplete.set(complete);
  }

  onTenantChange(tenantUuid: string): void {
    this.selectedTenantUuid.set(tenantUuid);
  }

  onRecaptchaV2Resolved(token: string): void {
    this.recaptchaFlow.onV2Resolved(token);
  }

  onRecaptchaV2Failed(): void {
    this.recaptchaFlow.onV2Failed();
    this.submissionError.set('Security verification failed. Please try again.');
    this.isSubmitting.set(false);
  }

  async onSubmit(): Promise<void> {
    const leadPayload = this.leadCapture()?.getLeadCaptureSubmissionPayload();
    const dateValue = this.trialBookingDate().trim();
    const tenantUuid = this.effectiveTenantUuid();

    if (!leadPayload || !dateValue || !tenantUuid) {
      this.submissionError.set(
        'Please fill in all required fields and select a trial booking date',
      );
      return;
    }

    let trialStartDate: string;
    try {
      trialStartDate = normalizeTrialStartDateForApi(dateValue);
    } catch {
      this.submissionError.set('Please select a valid trial booking date');
      return;
    }

    this.isSubmitting.set(true);
    this.submissionError.set(null);

    const submission: LeadCaptureWidgetSubmission = {
      ...leadPayload,
      widget_type: 'TrialBooking',
      data: {
        ...leadPayload.data,
        tenant_uuid: leadPayload.data.tenant_uuid || tenantUuid,
      },
      book_tour: '',
      trial_membership: {
        trial_start_date: trialStartDate,
        notes: String(leadPayload.data.note ?? ''),
      },
    };

    const company = this.companyUuid() ?? '';
    await this.recaptchaFlow.beginSubmit(company, tenantUuid, (captcha) => {
      void this.executeTrialBookingSubmit(submission, captcha);
    });
  }

  private async executeTrialBookingSubmit(
    submission: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.trialBookingService.submitTrialBooking(
          this.widgetId(),
          submission,
          captcha,
        ),
      );
      if (response?.id) {
        this.submitSuccess.set(true);
        this.submissionError.set(null);
        this.recaptchaFlow.resetChallenge();
        this.resetForm();
        const tenantUuid = submission.data.tenant_uuid || this.effectiveTenantUuid();
        const company = this.companyUuid();
        if (company && tenantUuid) {
          void this.recaptchaFlow.prepareForTenant(company, tenantUuid);
        }
      } else {
        throw new Error('No booking ID received from server');
      }
    } catch (error: unknown) {
      if (
        this.recaptchaFlow.handleRecaptchaError(error, (retryCaptcha) => {
          void this.executeTrialBookingSubmit(submission, retryCaptcha);
        })
      ) {
        this.isSubmitting.set(false);
        return;
      }

      this.submissionError.set(this.getSubmissionErrorMessage(error));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private getSubmissionErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const fromApi = extractWidgetApiErrorMessage(error.error);
      if (fromApi) {
        return fromApi;
      }
      return error.message || 'Failed to book trial. Please try again.';
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Failed to book trial. Please try again.';
  }

  private resetForm(): void {
    this.trialBookingDate.set('');
    this.surveyFormComplete.set(false);
    this.selectedTenantUuid.set(null);
    this.leadCapture()?.resetSurvey();
  }

  private async applyCompanyTheme(companyUuid: string): Promise<void> {
    if (!companyUuid) {
      return;
    }

    try {
      const company = await firstValueFrom(
        this.widgetService.getCompanyDetails(companyUuid),
      );
      const rawTheme = company.purchase_portal_theme;

      if (rawTheme == null || rawTheme === '') {
        return;
      }

      const companyTheme = parsePurchasePortalTheme(
        rawTheme as string | Record<string, unknown>,
      );
      const queryParams = this.readEmbedQueryParams();
      const { theme, isDark } = buildPurchasePortalTheme(
        companyTheme,
        queryParams,
      );

      applyPurchasePortalThemeToHost(
        this.elementRef.nativeElement,
        theme,
        isDark,
      );
    } catch {
      // Company theme is optional.
    }
  }

  private readEmbedQueryParams(): Record<string, string> {
    if (typeof window === 'undefined') {
      return {};
    }

    const queryParams: Record<string, string> = {};
    for (const [key, value] of new URLSearchParams(window.location.search)) {
      queryParams[key] = value;
    }

    return queryParams;
  }
}
